import { NavLink, Outlet, useMatch, useNavigate } from 'react-router';
import {
  ChartGantt,
  CircleCheck,
  House,
  Layers,
  ListTodo,
  Receipt,
  ScrollText,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { isVendorRole, type Capability } from '@rundown/policy';
import { Button, ToastProvider } from '../../components';
import { useAuth, useSignOut } from '../auth/useAuth';
import { useRealtime } from '../realtime/useRealtime';
import { NotificationBell } from '../notifications/NotificationBell';
import { UserMenu } from './UserMenu';
import { useWorkspace } from '../workspace/useWorkspace';
import { useCapabilities } from '../workspace/useCapability';
import { cx } from '../../lib/cx';
import { CommandPalette, SearchButton } from '../threads/CommandPalette';

/**
 * The frame every signed-in screen renders inside: a fixed sidebar, a sticky
 * search bar, and the routed screen.
 *
 * Laid out to match the design prototype — 224px rail, dense 13px type, the
 * search centred at 640px — because this product's screens are side-by-side
 * panes and drag surfaces, and a centred max-width column cannot hold them.
 *
 * It is also the single mount point for the realtime socket: one connection per
 * tab, opened once the user is known, torn down when they leave the app.
 *
 * ONE DELIBERATE DEPARTURE from the prototype: there is no Client/Noorix toggle.
 * In the prototype that switch exists so one mock can demonstrate both sides. In
 * the real product the answer comes from the caller's membership, and a client
 * being able to render the Noorix navigation — even with the server refusing the
 * data behind it — is a disclosure of what exists. The nav below is derived from
 * capabilities instead.
 */

interface NavItem {
  readonly to: string;
  readonly label: string;
  /**
   * One glyph per destination, so the rail can be scanned by shape rather than
   * read word by word. Chosen for what the screen *is* — a sprint is a date
   * range, a board is columns, a project is layers of work — rather than for
   * decoration, and drawn at the same weight as the 13px labels beside them so
   * the rail keeps its density.
   */
  readonly icon: LucideIcon;
  /** Absent means everyone in the workspace sees it. */
  readonly capability?: Capability;
  readonly end?: boolean;
  /**
   * False until the screen exists. Shown, but inert.
   *
   * The alternative — linking to a route that is not registered — sends people to
   * the 404 page, which reads as "this broke" rather than "this is not built
   * yet". Hiding them instead would misrepresent the product as smaller than it
   * is, and these are the screens the milestones are named after.
   */
  readonly built?: boolean;
}

/**
 * A run of destinations under one optional heading.
 *
 * The heading is what separates the week from the ledger: Audit trail, Month
 * end and Settings are places you go when someone asks a question about the
 * past or about the arrangement, not to get on with the work, and mixing them
 * into the same run made the rail read as eleven equal choices.
 */
interface NavGroup {
  readonly heading?: string;
  readonly items: readonly NavItem[];
}

/** The client's world: post, watch, rank, and see what is committed. */
const CLIENT_NAV: readonly NavGroup[] = [
  {
    items: [
      { to: '/', label: 'Home', icon: House, end: true, built: true },
      { to: '/plan', label: 'Plan', icon: ChartGantt, built: true },
      { to: '/backlog', label: 'Backlog', icon: ListTodo, built: true },
      { to: '/projects', label: 'Projects', icon: Layers, built: true },
      { to: '/completed', label: 'Completed', icon: CircleCheck, built: true },
    ],
  },
];

/** The delivery side's world: triage, build, commit, and account for it. */
const VENDOR_NAV: readonly NavGroup[] = [
  {
    items: [
      { to: '/', label: 'Home', icon: House, end: true, built: true },
      // One entry where there were three. Board, Planning and Sprints were
      // three doors into the same week, and which one you needed depended on
      // what the week's sprint happened to be doing.
      { to: '/plan', label: 'Plan', icon: ChartGantt, built: true },
      { to: '/backlog', label: 'Backlog', icon: ListTodo, built: true },
      // Projects was reachable only through a stripped-down list under
      // Settings, while the real screen was on the client's rail alone.
      { to: '/projects', label: 'Projects', icon: Layers, built: true },
      { to: '/completed', label: 'Completed', icon: CircleCheck, built: true },
    ],
  },
  {
    heading: 'Admin',
    items: [
      // The record of what has been done to the work, as opposed to the work.
      { to: '/audit', label: 'Audit trail', icon: ScrollText, capability: 'internal.read', built: true },
      { to: '/month-end', label: 'Month end', icon: Receipt, capability: 'billing.read', built: true },
      { to: '/settings', label: 'Settings', icon: Settings, capability: 'workspace.admin', built: true },
    ],
  },
];

export function AppShell() {
  const { user } = useAuth();
  const current = useWorkspace();
  const signOut = useSignOut();
  const navigate = useNavigate();
  // Above the early return: hooks cannot be called conditionally, and this one
  // is safe to call with no workspace — it returns an empty set.
  const capabilities = useCapabilities();

  useRealtime();

  const handleSignOut = () => {
    signOut.mutate(undefined, {
      // onSettled, not onSuccess: if the logout call fails the local session is
      // already cleared, and leaving the user on a dead screen is worse than
      // sending them to sign-in.
      onSettled: () => void navigate('/signin', { replace: true }),
    });
  };

  if (current === null) {
    return (
      <main className="flex min-h-[100dvh] items-center justify-center bg-surface px-4">
        <div className="max-w-sm text-center">
          <h1 className="fs-15 font-semibold text-ink">No workspace yet</h1>
          <p className="mt-1 fs-13 text-muted">
            Your account is not attached to a workspace. Ask your project lead to add you.
          </p>
          <Button className="mt-4" onClick={handleSignOut}>
            Sign out
          </Button>
        </div>
      </main>
    );
  }

  const { workspace, role } = current;
  // `isVendorRole`, not a string prefix. Which side of the product someone is
  // on is authorisation vocabulary, and @rundown/policy is where it is
  // defined — a prefix test here is a second definition that cannot be found
  // by grepping for the first when the role set changes.
  const isVendor = isVendorRole(role);
  const vendorName = workspace.vendorName;
  const threadMatch = useMatch('/t/:threadId');
  const newThreadMatch = useMatch('/new');
  const onHomeThread = threadMatch !== null || newThreadMatch !== null;
  // Groups whose every item was filtered out drop away with their heading —
  // a developer without billing or admin rights sees no empty "Admin" label.
  const nav = (isVendor ? VENDOR_NAV : CLIENT_NAV)
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.capability === undefined || capabilities.has(item.capability)),
    }))
    .filter((group) => group.items.length > 0);

  return (
    // Everything inside the shell can raise a toast. Mounted here rather than
    // at the router root so it sits inside RequireAuth — a confirmation has
    // nothing to confirm on the sign-in screen.
    <ToastProvider>
      {/* ⌘K / Ctrl+K from any screen — see CommandPalette. */}
      <CommandPalette />
      <div className="flex h-[100dvh] overflow-hidden">
        {/* --- Rail ------------------------------------------------------------
          Fixed 224px, never scrolls with the content. `flex-none` matters: a
          flex child defaults to shrinking, and the nav labels would wrap. */}
        <aside className="hidden w-56 flex-none flex-col gap-3.5 border-r border-line bg-surface-2 px-3 py-4 md:flex">
          <div className="flex items-center gap-2.5 px-2 py-1">
            <img src="/icon-512.png" alt="Rundown" className="size-7 flex-none rounded-lg" />
            <div className="min-w-0">
              <div className="truncate fs-13 font-bold text-ink">{workspace.name}</div>
              <div className="truncate fs-11 text-muted">
                {isVendor ? `${vendorName} workspace` : 'Client portal'}
              </div>
            </div>
          </div>

          <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto">
            {nav.map((group) => (
              <div key={group.heading ?? 'main'} className="flex flex-col gap-0.5">
                {group.heading !== undefined && (
                  <div className="mt-3 mb-0.5 border-t border-line pt-3 pl-2.5 fs-10 font-bold tracking-[0.06em] text-faint uppercase">
                    {group.heading}
                  </div>
                )}
                {group.items.map((item) =>
                  item.built === true ? (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        cx(
                          'flex items-center justify-between rounded-control px-2.5 py-[0.4375rem] fs-13',
                          // Home owns its thread routes too — /t/:id and /new
                          // are Home with a thread open, not another screen.
                          isActive || (item.to === '/' && onHomeThread)
                            ? 'bg-surface-4 font-semibold text-ink'
                            : 'font-normal text-ink-soft hover:bg-surface-3',
                        )
                      }
                    >
                      {/* Render-prop children, not just a className function: the
                        icon needs the active state too. Left at the label's
                        colour when active and a step quieter when not, so the
                        rail reads as one row per screen rather than a column of
                        glyphs competing with the words. */}
                      {({ isActive }) => (
                        <span className="flex min-w-0 items-center gap-2.5">
                          <item.icon
                            size={15}
                            strokeWidth={1.75}
                            aria-hidden="true"
                            className={cx(
                              'flex-none',
                              isActive || (item.to === '/' && onHomeThread) ? 'text-ink' : 'text-muted',
                            )}
                          />
                          <span className="whitespace-nowrap">{item.label}</span>
                        </span>
                      )}
                    </NavLink>
                  ) : (
                    <span
                      key={item.to}
                      aria-disabled="true"
                      title="Not built yet"
                      className="flex cursor-default items-center justify-between rounded-control px-2.5 py-[0.4375rem] fs-13 text-faint"
                    >
                      <span className="flex min-w-0 items-center gap-2.5">
                        <item.icon size={15} strokeWidth={1.75} aria-hidden="true" className="flex-none" />
                        <span className="whitespace-nowrap">{item.label}</span>
                      </span>
                      <span className="rounded-full bg-surface-4 px-[0.4375rem] fs-10 leading-[1.125rem] font-semibold text-muted">
                        soon
                      </span>
                    </span>
                  ),
                )}
              </div>
            ))}
          </nav>

          {/* The nav can now grow past the viewport without pushing anything
            important off the bottom, because nothing is anchored down there
            any more — identity moved to the header. It scrolls on its own. */}
        </aside>

        {/* --- Main -----------------------------------------------------------
          min-w-0 is load-bearing: without it this flex child refuses to shrink
          below its content and the whole page scrolls sideways instead of the
          panes inside it. */}
        {/* The header is a fixed band and the routed screen owns the scroll
          region beneath it, rather than the whole column scrolling as one.
          That is what lets the Request screen be a fixed-height two-pane layout
          whose panes scroll independently — impossible if the page itself
          scrolls. min-h-0 on the scroll region is load-bearing: a flex child
          defaults to min-height:auto and refuses to shrink below its content,
          so overflow never engages and the layout silently grows instead. */}
        <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {/* Search is ⌘K / Ctrl+K from anywhere (CommandPalette), so the
            header carries only a small button that says so — a second search
            box here was a third way to do the same thing. */}
          <div className="z-10 flex flex-none items-center gap-3 border-b border-surface-3 bg-base px-4 py-2.5 md:px-6">
            <div className="flex flex-1 items-center justify-end gap-2">
              <SearchButton />
              <NotificationBell />
              <UserMenu
                name={user?.name ?? ''}
                email={user?.email ?? ''}
                role={role}
                onSignOut={handleSignOut}
                signingOut={signOut.isPending}
              />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </ToastProvider>
  );
}
